<template>
  <div class="fill-height d-flex align-center justify-center" style="flex-direction: column;">
    <AppLoader v-if="isFilePending" />
    <template v-else>
      <img
        src="@/assets/img/closd_logo.svg"
        width="150"
        alt="Closd Logo"
      />
      <form
        v-if="isUsingFormSubmit"
        ref="fileDownloadForm"
        :action="link"
        method="post"
      >
        <!--
          The bearer token and path token shouldn't conflict
          and this enables using this auto form for paths that only need the bearer token
        -->
        <input
          v-if="bearerToken"
          type="hidden"
          :value="bearerToken"
          name="api_token"
        />
      </form>
      <span class="clickable mt-4" @click="downloadFile">
        {{ $t('login.views.DownloadFromToken.clickForDl') }}
      </span>
    </template>
  </div>
</template>

<script>
  import axios from 'axios'

  import AppLoader from '@/common/AppLoader.vue'
  import { ACCEPTED_DOWNLOAD_TYPES } from '@/common/constants'
  import { toast } from '@/common/utils/toast'
  import { EXPIRED_LINK_ROUTE_NAME } from '@/router'

  export default {
    name: 'DownloadFromToken',
    components: { AppLoader },
    props: {
      mnemo: String,
      /**
       * Token used as a prop. Can be empty but for the link to work the user should have an api_token
       */
      token: {
        type: String,
      },
    },
    data() {
      return {
        isFilePending: false,
      }
    },
    computed: {
      bearerToken () {
        return axios.defaults.headers.common.Authorization
      },
      link () {
        return `${import.meta.env.VUE_APP_API_URL}${this.$route.path}`
      },
      isUsingFormSubmit () {
        return !this.token
      },
    },
    mounted() {
      this.downloadFile()
    },
    methods: {
      downloadFile () {
        if (this.isUsingFormSubmit) {
          this.$refs.fileDownloadForm.submit()
        } else {
          this.getFileOrRedirect()
        }
      },
      async getFileOrRedirect () {
        this.isFilePending = true
        try {
          const response = await axios.post(this.link, null, {
            headers: {
              Authorization: this.bearerToken,
            },
            responseType: 'blob',
          })

          if (
            response.data
            && ACCEPTED_DOWNLOAD_TYPES.includes(response.headers['content-type'])
          ) {
            const contentType = response.headers['content-type']
            const blob = new Blob([response.data], { type: contentType })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url

            const fileName = response.data.fileName || 'document.zip'
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
          } else {
            toast.error(this.$t('login.views.DownloadFromToken.invalidFile'))
          }
        } catch (error) {
          console.error('error:', error)

          // Handle specific error codes
          if (error.response?.status === 401) {
            const reader = new FileReader()
            reader.onload = () => {
              try {
                const errorData = JSON.parse(reader.result)
                if (errorData.errorCode === 'ERR_TOKEN_EXPIRED') {
                  this.$router.replace({
                    name: EXPIRED_LINK_ROUTE_NAME,
                    query: {
                      expirationDate: errorData.expirationDate,
                      expirationHour: errorData.expirationHour,
                    },
                  })
                } else {
                  toast.error(this.$t('login.views.DownloadFromToken.genericError'))
                }
              } catch (parseError) {
                console.error('JSON parsing error:', parseError)
                toast.error(this.$t('login.views.DownloadFromToken.genericError'))
              }
            }
            reader.readAsText(error.response.data)
          } else if (error.response?.status === 404) {
            toast.error(this.$t('login.views.DownloadFromToken.notFound'))
          } else {
            toast.error(this.$t('login.views.DownloadFromToken.genericError'))
          }
        } finally {
          this.isFilePending = false
        }
      },
    },
  }
</script>
